import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import {
  hasDecided,
  setConsent,
  getConsent,
  initConsent,
  OPEN_CONSENT_EVENT,
} from "@/lib/consent";
import { useTranslation } from "react-i18next";

// Self-contained 4-language copy (kept here to avoid touching the locale
// bundles; mirrors the LanguageBanner pattern).
const T = {
  pt: {
    title: "As tuas escolhas de cookies",
    body: "Usamos cookies essenciais para o site funcionar. Com a tua autorização, usamos também cookies de análise (Google Analytics, Microsoft Clarity) e de chat de apoio (Tawk.to) para melhorar a experiência.",
    accept: "Aceitar tudo",
    reject: "Rejeitar opcionais",
    manage: "Gerir",
    save: "Guardar escolhas",
    essential: "Essenciais",
    essential_desc: "Necessários para o funcionamento e segurança (sempre ativos).",
    optional: "Análise e chat de apoio",
    optional_desc: "Estatísticas de utilização e chat de apoio ao cliente.",
    always_on: "Sempre ativos",
  },
  en: {
    title: "Your cookie choices",
    body: "We use essential cookies to run the site. With your consent, we also use analytics cookies (Google Analytics, Microsoft Clarity) and support-chat cookies (Tawk.to) to improve your experience.",
    accept: "Accept all",
    reject: "Reject optional",
    manage: "Manage",
    save: "Save choices",
    essential: "Essential",
    essential_desc: "Required for the site to work and stay secure (always on).",
    optional: "Analytics & support chat",
    optional_desc: "Usage statistics and customer support chat.",
    always_on: "Always on",
  },
  es: {
    title: "Tus opciones de cookies",
    body: "Usamos cookies esenciales para que el sitio funcione. Con tu permiso, también usamos cookies de análisis (Google Analytics, Microsoft Clarity) y de chat de soporte (Tawk.to) para mejorar tu experiencia.",
    accept: "Aceptar todo",
    reject: "Rechazar opcionales",
    manage: "Gestionar",
    save: "Guardar opciones",
    essential: "Esenciales",
    essential_desc: "Necesarias para el funcionamiento y la seguridad (siempre activas).",
    optional: "Análisis y chat de soporte",
    optional_desc: "Estadísticas de uso y chat de atención al cliente.",
    always_on: "Siempre activas",
  },
  fr: {
    title: "Vos choix de cookies",
    body: "Nous utilisons des cookies essentiels au fonctionnement du site. Avec votre accord, nous utilisons aussi des cookies d'analyse (Google Analytics, Microsoft Clarity) et de chat d'assistance (Tawk.to) pour améliorer votre expérience.",
    accept: "Tout accepter",
    reject: "Refuser les optionnels",
    manage: "Gérer",
    save: "Enregistrer",
    essential: "Essentiels",
    essential_desc: "Nécessaires au fonctionnement et à la sécurité (toujours actifs).",
    optional: "Analyse et chat d'assistance",
    optional_desc: "Statistiques d'utilisation et chat d'assistance client.",
    always_on: "Toujours actifs",
  },
};

export function CookieConsent() {
  const { i18n } = useTranslation();
  const lang = String(i18n.language || "pt").slice(0, 2).toLowerCase();
  const t = T[lang] || T.pt;

  const [open, setOpen] = useState(() => !hasDecided());
  const [managing, setManaging] = useState(false);
  const [optional, setOptional] = useState(() => {
    const c = getConsent();
    return c ? !!c.analytics : true;
  });

  // Footer "Cookies" link re-opens the panel; also load trackers if the user
  // already consented in a previous visit.
  useEffect(() => {
    initConsent();
    const reopen = () => {
      const c = getConsent();
      setOptional(c ? !!c.analytics : true);
      setManaging(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  if (!open) return null;

  const acceptAll = () => { setConsent(true); setOpen(false); };
  const rejectAll = () => { setConsent(false); setOpen(false); };
  const saveChoices = () => { setConsent(optional); setOpen(false); };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[10002] p-3 sm:p-4"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur-md shadow-2xl text-zinc-100">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm sm:text-base mb-1">{t.title}</p>
              <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed">{t.body}</p>
            </div>
          </div>

          {managing && (
            <div className="mt-4 space-y-3" data-testid="cookie-consent-manage-panel">
              <div className="flex items-start justify-between gap-3 rounded-lg bg-white/5 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t.essential}</p>
                  <p className="text-xs text-zinc-400">{t.essential_desc}</p>
                </div>
                <span className="text-xs font-semibold text-zinc-400 flex-shrink-0 mt-0.5">{t.always_on}</span>
              </div>
              <label className="flex items-start justify-between gap-3 rounded-lg bg-white/5 p-3 cursor-pointer">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{t.optional}</p>
                  <p className="text-xs text-zinc-400">{t.optional_desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={optional}
                  onChange={(e) => setOptional(e.target.checked)}
                  className="mt-1 h-4 w-4 flex-shrink-0 accent-primary"
                  data-testid="cookie-consent-optional-toggle"
                />
              </label>
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-2">
            {!managing ? (
              <button
                onClick={() => setManaging(true)}
                className="order-3 sm:order-1 rounded-full border border-white/20 px-4 py-2 text-xs sm:text-sm font-medium hover:bg-white/10 transition-colors"
                data-testid="cookie-consent-manage-btn"
              >
                {t.manage}
              </button>
            ) : (
              <button
                onClick={saveChoices}
                className="order-1 rounded-full border border-white/20 px-4 py-2 text-xs sm:text-sm font-medium hover:bg-white/10 transition-colors"
                data-testid="cookie-consent-save-btn"
              >
                {t.save}
              </button>
            )}
            <button
              onClick={rejectAll}
              className="order-2 rounded-full border border-white/20 px-4 py-2 text-xs sm:text-sm font-medium hover:bg-white/10 transition-colors"
              data-testid="cookie-consent-reject-btn"
            >
              {t.reject}
            </button>
            <button
              onClick={acceptAll}
              className="order-1 sm:order-3 rounded-full bg-primary px-5 py-2 text-xs sm:text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="cookie-consent-accept-btn"
            >
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
